import { useQuery } from "@rocicorp/zero/react";
import { useEffect, useState } from "react";
import { queries } from "../../shared/queries";
import { config } from "../environment";

export function selectConnectorSealingKey<
  T extends { tenantId: string; publicKey: string; algorithm: string | null },
>(
  tenantId: string | undefined,
  legacy: T | undefined,
  pq: T | undefined,
  previous: T | undefined,
  rootAlgorithm: typeof config.credentialRootAlgorithm
): T | undefined {
  const held = previous?.tenantId === tenantId ? previous : undefined;
  const candidate = rootAlgorithm === "mlkem1024" ? pq : legacy;
  const key =
    !candidate || candidate.tenantId !== tenantId
      ? held
      : held?.algorithm === "mlkem1024" && candidate.algorithm !== "mlkem1024"
        ? held
        : candidate;
  return rootAlgorithm === "mlkem1024" && key?.algorithm !== "mlkem1024"
    ? undefined
    : key;
}

export function useConnectorSealingKey() {
  const [rootAlgorithm, setRootAlgorithm] = useState(
    config.credentialRootAlgorithm
  );
  // A self-hosted upgrade can activate PQ after this tab loads.
  useEffect(() => {
    if (!config.credentialSealingRequired || rootAlgorithm === "mlkem1024")
      return;
    const refresh = () => {
      void fetch("/ui-api/environment", { cache: "no-store" })
        .then((response) => response.json())
        .then((environment: { credentialRootAlgorithm?: string }) => {
          if (environment.credentialRootAlgorithm === "mlkem1024")
            setRootAlgorithm("mlkem1024");
        })
        .catch(() => {});
    };
    refresh();
    const interval = window.setInterval(refresh, 60_000);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
    };
  }, [rootAlgorithm]);

  const [tenant] = useQuery(queries.currentTenant());
  const [legacyKeys, legacyResult] = useQuery(queries.tenantSealingKeys());
  const [pqKeys, pqResult] = useQuery(
    rootAlgorithm === "mlkem1024"
      ? queries.tenantPostQuantumSealingKeys()
      : undefined
  );
  const legacy = legacyKeys?.find((key) => key.domain === "credentials");
  const pq = pqKeys?.find((key) => key.domain === "credentials");
  const [previousKey, setPreviousKey] = useState<typeof legacy>();
  const key = selectConnectorSealingKey(
    tenant?.id,
    legacy,
    pq,
    previousKey,
    rootAlgorithm
  );
  if (
    key &&
    (key.publicKey !== previousKey?.publicKey ||
      key.tenantId !== previousKey.tenantId)
  )
    setPreviousKey(key);
  if (rootAlgorithm === "mlkem1024" && !key) return undefined;
  return (
    key ??
    (tenant &&
    legacyResult.type === "complete" &&
    (rootAlgorithm !== "mlkem1024" || pqResult.type === "complete")
      ? null
      : undefined)
  );
}
